import React, { FC, useState, useRef } from 'react'
import styles from './Search.module.scss'
import { Icon } from 'components/Icon'
import { Button } from 'components/Button'
import { useId } from '@reach/auto-id'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import { useOnClickOutside } from 'hooks/useClickOutside'

type SearchProps = {
  value?: string
  onSubmit: (value: string) => void
}

export const Search: FC<SearchProps> = (props) => {
  const { t } = useTranslation()
  const [isOpen, setOpen] = useState(props.value ?? false)
  const [value, setValue] = useState(props.value ?? '')
  const inputId = useId()
  const searchRef = useRef<HTMLDivElement>({} as HTMLDivElement)

  const handleBlur = () => {
    if (value) return
    setOpen(false)
  }

  useOnClickOutside(searchRef, handleBlur)

  const searchBarStyles = cx({
    [styles.searchBar]: styles.searchBar,
    [styles.filled]: value,
  })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    props.onSubmit(value)

    // If the input is empty, the search bar is collapsed back to a button
    if (!value) {
      setOpen(false)
    }
  }

  return (
    <div className={searchBarStyles} ref={searchRef}>
      {!isOpen && (
        <button
          className={styles.searchToggleButton}
          aria-label={t('user.search.open')}
          onClick={() => setOpen(true)}
        >
          <Icon name="search-line" size="xl" className={styles.icon} />
        </button>
      )}

      {isOpen && (
        <form onSubmit={handleSubmit}>
          <span>
            <Icon name="search-line" size="xl" className={styles.icon} />
          </span>

          <input
            id={inputId}
            type="text"
            value={value}
            onChange={handleChange}
            placeholder={t('user.search.label')}
            aria-label={t('user.search.label')}
          />

          <Button type="submit" className={styles.searchButton}>
            {t('user.search.cta')}
          </Button>
        </form>
      )}
    </div>
  )
}

Search.displayName = 'Search'
